import dayjs from "dayjs";
import { X, Printer } from "lucide-react";

const pretty = (k) => String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function isEmpty(v) {
  if (v === null || v === undefined || v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.values(v).every(isEmpty);
  return false;
}

function renderTable(rows) {
  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))].filter((c) => rows.some((r) => !isEmpty(r[c]) && typeof r[c] !== "object")).slice(0, 6);
  return (
    <table className="w-full text-xs border border-[#E5DFD3] mt-1">
      <thead><tr>{cols.map((c) => <th key={c} className="border-b border-[#E5DFD3] bg-[#F5F2EB] px-2 py-1 text-left font-semibold text-[#4E342E]">{pretty(c)}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c} className="border-b border-[#F1EEE7] px-2 py-1">{isEmpty(r[c]) ? "-" : String(r[c])}</td>)}</tr>)}</tbody>
    </table>
  );
}

function renderValue(v, depth) {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) {
    if (v.every((x) => typeof x !== "object")) return v.join(", ");
    const rows = v.filter((x) => x && typeof x === "object" && !isEmpty(x));
    return rows.length === 0 ? "-" : renderTable(rows);
  }
  if (typeof v === "object") return renderEntries(v, depth + 1);
  return String(v);
}

function renderEntries(obj, depth = 0) {
  const entries = Object.entries(obj || {}).filter(([, v]) => !isEmpty(v));
  if (entries.length === 0) return <div className="text-xs text-[#84675B] italic">Not provided</div>;
  const wide = (v) => (Array.isArray(v) && v.some((x) => typeof x === "object")) || (typeof v === "object" && !Array.isArray(v));
  return (
    <div className={depth > 0 ? "pl-3 border-l-2 border-[#F1EEE7] space-y-1 w-full" : "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5"}>
      {entries.map(([k, v]) => (
        <div key={k} className={`text-sm flex gap-2 items-baseline min-w-0 ${wide(v) ? "sm:col-span-2 flex-col" : ""}`}>
          <span className="text-[#7B6157] shrink-0">{pretty(k)}:</span>
          <span className="text-[#3E2723] font-medium break-words min-w-0 flex-1 w-full">{renderValue(v, depth)}</span>
        </div>
      ))}
    </div>
  );
}

export default function IntakePdfView({ form, tabs, onClose }) {
  const demo = form.demographics || {};
  const name = `${demo.first_name || ""} ${demo.last_name || ""}`.trim() || "New Client";
  const doPrint = () => {
    document.body.classList.add("print-face-sheet");
    window.print();
    setTimeout(() => document.body.classList.remove("print-face-sheet"), 300);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#3E2723]/60 backdrop-blur-sm overflow-y-auto" data-testid="intake-pdf-view">
      <div className="sticky top-0 z-10 flex items-center justify-between bg-[#3E2723] px-6 py-3 print:hidden">
        <div className="font-heading font-bold text-white">PDF View - Intake Form</div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="pdf-print-btn" onClick={doPrint}
            className="inline-flex items-center gap-2 rounded-full bg-[#D9C7B2] px-4 py-2 text-sm font-semibold text-[#3E2723] hover:bg-[#EDE0CE] transition-colors">
            <Printer size={14} /> Print / Save PDF
          </button>
          <button type="button" data-testid="pdf-close-btn" aria-label="Close PDF view" onClick={onClose}
            className="h-9 w-9 rounded-full flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
      </div>

      <div data-print-area className="mx-auto my-8 w-[850px] max-w-[95vw] rounded-xl bg-white p-10 shadow-2xl">
        <div className="flex items-start justify-between border-b-2 border-[#3E2723] pb-4 mb-6">
          <div>
            <div className="font-heading text-2xl font-bold text-[#3E2723]">Client Intake Form</div>
            <div className="text-sm text-[#6D4C41] mt-1">{name}{form.account_number ? ` · #${form.account_number}` : ""}</div>
          </div>
          <div className="text-right text-xs text-[#7B6157]">
            <div className="font-heading font-bold text-[#3E2723] text-sm">Cocoa EHR</div>
            <div>Generated {dayjs().format("MM/DD/YYYY h:mm A")}</div>
          </div>
        </div>

        {tabs.map((t) => (
          <div key={t.key} className="mb-6" style={{ breakInside: "avoid" }}>
            <h3 className="font-heading text-[13px] font-bold text-[#3E2723] uppercase tracking-wider border-b-2 border-[#D9C7B2] pb-1 mb-2">{t.label}</h3>
            {isEmpty(form[t.key]) ? <div className="text-xs text-[#84675B] italic">Not provided</div>
              : renderEntries(Array.isArray(form[t.key]) ? { items: form[t.key] } : form[t.key])}
          </div>
        ))}

        <div className="mt-8 pt-3 border-t border-[#EFEBE0] text-[10px] text-[#84675B]">
          Cocoa EHR - Confidential: this document contains protected health information (PHI) and is intended only for authorized recipients.
        </div>
      </div>
    </div>
  );
}
